/* eslint-disable no-case-declarations */
import {
  Component,
  computed,
  effect,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { IUtmParametersResponse } from '@g99-product-web-ui/shared-interface-lib';

import {
  DEFAULT_UTM_SUGGESTIONS,
  UTM_FIELD_MAPPING,
  UTM_INPUT_SVG_ICONS,
  UTM_TYPE_COLOR_MAP,
  UTM_TYPE_MAPPING,
} from '../../constants/utm-input.constant';

// UTM Parameter Type
export interface UtmParam {
  type: string;
  value: string;
}

@Component({
  selector: 'g99-lib-utm-input',
  templateUrl: './utm-input.component.html',
  styleUrl: './utm-input.component.scss',
  standalone: false,
})
export class UtmInputComponent {
  // ============================================
  // INPUTS
  // ============================================

  /**
   * Accept pre-selected parameters from parent (from API)
   * Can pass either:
   * 1. UtmParam[] format: [{ type: 'source', value: 'google' }]
   * 2. API format: { utmSource: ['google'], utmMedium: ['cpc'] }
   */
  public preSelectedParams = input<
    UtmParam[] | Partial<IUtmParametersResponse>
  >([]);

  /**
   * Available suggestions for autocomplete dropdown
   * This is the list of ALL possible UTM parameters the user can choose from
   * Pass this from parent component
   */
  public availableSuggestions = input<UtmParam[]>(DEFAULT_UTM_SUGGESTIONS);

  public placeholder = input<string>(
    'Type to search and add UTM parameters...'
  );
  public disabled = input<boolean>(false);

  // ============================================
  // OUTPUTS (Events)
  // ============================================

  /**
   * Two-way binding for selected parameters
   * Use: [(selectedParams)]="myParams" in parent
   */
  public selectedParams = model<UtmParam[]>([]);

  /**
   * Event emitter when selection changes
   * Emits both component format and API format
   */
  public handleSelectionChange = output<{
    params: UtmParam[];
    apiFormat: IUtmParametersResponse;
  }>();

  // ============================================
  // SIGNALS (Internal state)
  // ============================================

  protected inputValue = signal<string>('');
  protected isFocused = signal<boolean>(false);
  protected highlightedIndex = signal<number>(-1); // ✅ NEW: Track keyboard navigation

  // ============================================
  // CONSTANTS
  // ============================================
  protected svgIcons = UTM_INPUT_SVG_ICONS;

  protected filteredSuggestions = computed(() => {
    const search = this.inputValue().toLowerCase().trim();
    const suggestions = this.availableSuggestions();

    if (!search) {
      // Show all available suggestions when no search
      const selected = new Set(
        this.selectedParams().map((p) => `${p.type}:${p.value}`)
      );
      return suggestions.filter((param) => {
        const key = `${param.type}:${param.value}`;
        return !selected.has(key);
      });
    }

    const selected = new Set(
      this.selectedParams().map((p) => `${p.type}:${p.value}`)
    );

    return suggestions.filter((param) => {
      const key = `${param.type}:${param.value}`;
      const matchesSearch =
        param.type.toLowerCase().includes(search) ||
        param.value.toLowerCase().includes(search);
      const notSelected = !selected.has(key);
      return matchesSearch && notSelected;
    });
  });

  protected showSuggestions = computed(
    () => this.isFocused() && this.filteredSuggestions().length > 0
  );

  private _isInitialized = signal<boolean>(false);

  // ============================================
  // CONSTRUCTOR & EFFECTS
  // ============================================

  constructor() {
    // Effect to initialize selected params from parent (ONLY ONCE)
    effect(
      () => {
        const preSelected = this.preSelectedParams();

        // Only process if not yet initialized and there are pre-selected params
        if (!this._isInitialized() && preSelected) {
          let formattedParams: UtmParam[] = [];

          if (Array.isArray(preSelected)) {
            formattedParams = [...preSelected];
          } else if (Object.keys(preSelected).length > 0) {
            // Convert API format to component format
            formattedParams = UtmInputComponent.formatApiResponse(preSelected);
          }

          if (formattedParams.length > 0) {
            this.selectedParams.set(formattedParams);
          }

          // Mark as initialized to prevent re-processing
          this._isInitialized.set(true);
        }
      },
      { allowSignalWrites: true }
    );

    // Effect to emit onChange event whenever selectedParams changes
    // BUT skip the initial emission during initialization
    effect(() => {
      const params = this.selectedParams();

      // Only emit if already initialized (skip initial load)
      if (this._isInitialized()) {
        const apiFormat = UtmInputComponent.formatToApiResponse(params);
        this.handleSelectionChange.emit({ params, apiFormat });
      }
    });

    // ✅ NEW: Reset highlighted index when suggestions change
    effect(
      () => {
        this.filteredSuggestions(); // Track dependency
        this.highlightedIndex.set(-1); // Reset highlight when suggestions change
      },
      { allowSignalWrites: true }
    );
  }

  // ============================================
  // PUBLIC STATIC UTILITY METHODS
  // ============================================

  /**
   * Utility method to convert API response format to UtmParam array
   * @param apiResponse - API response in format { utmSource: ['google', 'facebook'], utmMedium: ['cpc'] }
   * @returns Array of UtmParam objects
   */
  public static formatApiResponse(
    apiResponse: Partial<IUtmParametersResponse>
  ): UtmParam[] {
    const params: UtmParam[] = [];

    for (const [key, values] of Object.entries(apiResponse)) {
      const type = UTM_FIELD_MAPPING[key];
      if (type && Array.isArray(values)) {
        values.forEach((value) => {
          if (value && value.trim()) {
            params.push({ type, value: value.trim() });
          }
        });
      }
    }

    return params;
  }

  /**
   * Utility method to convert UtmParam array back to API response format
   * @param params - Array of UtmParam objects
   * @returns API response format
   */
  public static formatToApiResponse(
    params: UtmParam[]
  ): IUtmParametersResponse {
    const response: IUtmParametersResponse = {} as IUtmParametersResponse;

    params.forEach((param) => {
      const fieldName = UTM_TYPE_MAPPING[
        param.type
      ] as keyof IUtmParametersResponse;
      if (fieldName) {
        if (!response[fieldName]) {
          response[fieldName] = [];
        }
        response[fieldName]?.push(param.value);
      }
    });

    return response;
  }

  // ============================================
  // PUBLIC INSTANCE METHODS
  // ============================================

  /**
   * Set selected params programmatically
   * @param params - Array of UtmParam objects or API response format
   */
  public setSelectedParams(params: UtmParam[] | IUtmParametersResponse): void {
    if (Array.isArray(params)) {
      this.selectedParams.set([...params]);
    } else {
      const formattedParams = UtmInputComponent.formatApiResponse(params);
      this.selectedParams.set(formattedParams);
    }

    // Mark as initialized if setting params programmatically
    if (!this._isInitialized()) {
      this._isInitialized.set(true);
    }
  }

  /**
   * Get selected params in API response format
   */
  public getApiFormat(): IUtmParametersResponse {
    return UtmInputComponent.formatToApiResponse(this.selectedParams());
  }

  // ============================================
  // PROTECTED METHODS (used in template)
  // ============================================

  protected onInputChange(value: string): void {
    this.inputValue.set(value);
  }

  protected onInputFocus(): void {
    this.isFocused.set(true);
  }

  protected onInputBlur(): void {
    // Increase delay to allow click/mousedown events on suggestions
    setTimeout(() => {
      this.isFocused.set(false);
      this.highlightedIndex.set(-1); // Reset highlight on blur
    }, 300); // Increased from 200ms to 300ms
  }

  // ✅ NEW: Handle keyboard navigation
  protected onKeyDown(event: KeyboardEvent): void {
    const suggestions = this.filteredSuggestions();

    if (!this.showSuggestions() || suggestions.length === 0) {
      return;
    }

    const currentIndex = this.highlightedIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        // Move down in the list
        const nextIndex =
          currentIndex < suggestions.length - 1 ? currentIndex + 1 : 0;
        this.highlightedIndex.set(nextIndex);
        this._scrollToHighlighted(nextIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        // Move up in the list
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : suggestions.length - 1;
        this.highlightedIndex.set(prevIndex);
        this._scrollToHighlighted(prevIndex);
        break;

      case 'Enter':
        event.preventDefault();
        // Select highlighted item or first item if none highlighted
        const indexToSelect = currentIndex >= 0 ? currentIndex : 0;
        if (suggestions[indexToSelect]) {
          this.addParam(suggestions[indexToSelect]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        // Close suggestions
        this.isFocused.set(false);
        this.highlightedIndex.set(-1);
        break;
    }
  }

  // ✅ NEW: Check if suggestion is highlighted
  protected isHighlighted(index: number): boolean {
    return this.highlightedIndex() === index;
  }

  protected addParam(param: UtmParam): void {
    const current = this.selectedParams();
    const exists = current.some(
      (p) => p.type === param.type && p.value === param.value
    );

    if (!exists) {
      this.selectedParams.set([...current, param]);
    }

    this.inputValue.set('');
    this.highlightedIndex.set(-1); // Reset highlight after selection
  }

  protected removeParam(param: UtmParam): void {
    const current = this.selectedParams();
    const filtered = current.filter(
      (p) => !(p.type === param.type && p.value === param.value)
    );
    this.selectedParams.set(filtered);
  }

  protected clearAll(): void {
    this.selectedParams.set([]);
    this.inputValue.set('');
    this.highlightedIndex.set(-1);
  }

  protected getTypeColor(type: string): string {
    return UTM_TYPE_COLOR_MAP[type] || 'tag-gray';
  }

  protected trackByFullParam(index: number, param: UtmParam): string {
    return `${param.type}:${param.value}`;
  }

  // ============================================
  // PRIVATE METHODS
  // ============================================

  /**
   * Scroll highlighted suggestion into view
   */
  private _scrollToHighlighted(index: number): void {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      const suggestionElement = document.querySelector(
        `.utm-tag-input__suggestion:nth-child(${index + 1})`
      );
      if (suggestionElement) {
        suggestionElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }, 0);
  }
}
